import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateRequest } from '../../api/production-request-api/index.js';
import { removeRequestFromCache } from '../../utils';
import { useAppContext } from '../../providers';
import { QUERY_KEYS, STATUSES } from '../../data/constants';

/**
 * Hook containing mutation function for updating a Production Request.
 */
export const useUpdateRequest = () => {
  const { selectedResource, selectedLocation } = useAppContext();
  const queryClient = useQueryClient();

  return useMutation({
    /**
     * mutateFn - Function to call the updateRequest api.
     * @param {object} params
     * @param {Pella.ProductionRequestAPI.Request['id']} params.id - The ID of the Production Request to update.
     * @param {Pella.ProductionRequestAPI.Request['status']} params.status - The new status of the Production Request.
     * @param {string | null} [params.onHoldReason] - Hold reason label, only set when placing on hold via the reason modal.
     */
    mutationFn: ({ id, status, onHoldReason }) => {
      const input = /** @type {Local.RequestUpdateInput} */ ({
        status,
        ...(selectedResource && { resourceId: selectedResource }),
        ...(onHoldReason !== null && { onHoldReason }),
      });
      return updateRequest(id, input);
    },
    onSuccess: (_data, variables) => {
      // Optimistically update the cached requests data for both resource and location queries
      if (selectedResource)
        queryClient.setQueryData(
          [QUERY_KEYS.REQUESTS_BY_RESOURCE, selectedResource],
          (/** @type {Pella.ProductionRequestAPI.GetRequestsOutput} */ oldData) =>
            removeRequestFromCache(oldData, variables.id),
        );

      if (selectedLocation)
        queryClient.setQueryData(
          [QUERY_KEYS.REQUESTS_BY_LOCATION, selectedLocation],
          (/** @type {Pella.ProductionRequestAPI.GetRequestsOutput} */ oldData) =>
            removeRequestFromCache(oldData, variables.id),
        );

      if (variables.status === STATUSES.COMPLETED)
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPLETED_REQUESTS_BY_RESOURCE, selectedResource] });
    },
  });
};
